import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AuthContext } from '../components/AuthContext';

// Create a context for dashboard data
const DashboardDataContext = createContext();

const DATA_ENDPOINTS = {
  serviceQueue: '/activity/daily-summary',
  dailyProgress: '/activity/daily-summary',
  estimateData: '/activity/estimate-counts',
  productCategory: '/activity/open-cases',
  samsungServiceData: '/samsung/service-analysis',
  userData: '/user'
};

const CACHE_TTL = 5 * 60 * 1000;

export function DashboardDataProvider({ children }) {
  const cacheRef = useRef({});
  // Get saved rotation state from localStorage if available
  const getSavedRotationEnabled = () => {
    const saved = localStorage.getItem('dashboardRotationEnabled');
    return saved === 'true'; // Convert string to boolean
  };
  
  const [dashboardState, setDashboardState] = useState({
    serviceQueue: { loading: false, data: null, error: null },
    dailyProgress: { loading: false, data: null, error: null },
    estimateData: { loading: false, data: null, error: null },
    // Ensure productCategoryData is always defined with a default structure
    productCategoryData: { loading: false, data: [], error: null },
    productCategory: { loading: false, data: null, error: null }, // legacy, for compatibility
    samsungServiceData: { loading: false, data: null, error: null },
    userData: { loading: false, data: null, error: null },
    scheduler: { loading: false, data: null, error: null },
    // Use saved rotation state if available
    rotation: { 
      enabled: getSavedRotationEnabled(), 
      interval: Number(localStorage.getItem('dashboardRotationInterval') || 300), 
      isSaving: false 
    },
    lastZone: parseInt(localStorage.getItem('lastDashboardZone') || '0'),
    zones: [],
    activeWidgets: []
  });
  const [isDashboardActive, setIsDashboardActive] = useState(false);
  const lastSchedulerSaveRef = useRef(0);
  const { logout } = useContext(AuthContext);

  const getTokenAndUsername = useCallback(() => {
    try {
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('token='))
        ?.split('=')[1];
      if (!token) return { token: null, username: null }; // Do not throw, just return nulls
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const payload = JSON.parse(jsonPayload);
      return { token, username: payload.sub || payload.username };
    } catch (err) {
      if (logout) logout();
      return { token: null, username: null }; // Do not throw, just return nulls
    }
  }, [logout]);

  const updateLastFetched = (key) => {
    cacheRef.current[`${key}_lastFetched`] = Date.now();
  };

  const fetchData = useCallback(async (key, { token, username, force = false }) => {
    try {
      // If no token, do not attempt to fetch, just return null
      if (!token) {
        console.warn(`[DashboardDataContext] No token found, skipping fetch for ${key}`);
        return null;
      }
      const endpoint = DATA_ENDPOINTS[key];
      if (!endpoint) throw new Error('Unknown endpoint');
      const now = Date.now();
      if (!force && cacheRef.current[key] && now - cacheRef.current[key].ts < CACHE_TTL) {
        return cacheRef.current[key].data;
      }
      let url = `${process.env.REACT_APP_API_URL}${endpoint}`;
      let options = { headers: { 'Content-Type': 'application/json' } };
      if (key === 'userData') {
        url += `?username=${username}&token=${token}`;
        options.method = 'GET';
      } else {
        options.method = 'POST';
        options.body = JSON.stringify({ username, token });
        options.headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(url, options);
      if (res.status === 401 || res.status === 403) {
        if (logout) logout();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error(`Failed to fetch ${key}`);
      const data = await res.json();
      cacheRef.current[key] = { data, ts: Date.now() };
      updateLastFetched(key);
      // NEW: Sync productCategoryData state immediately if this is productCategory
      if (key === 'productCategory') {
        setDashboardState(prev => ({
          ...prev,
          productCategoryData: { loading: false, data, error: null },
          productCategory: { loading: false, data, error: null } // <-- keep legacy in sync
        }));
      }
      return data;
    } catch (err) {
      // Suppress network errors (e.g., backend down)
      if (err instanceof TypeError && err.message && err.message.includes('Failed to fetch')) {
        console.warn(`[DashboardDataContext] Network error while fetching ${key}:`, err);
        // Do not throw, just return null to indicate data unavailable
        return null;
      }
      if (err.message && (err.message.includes('No token found') || err.message.includes('Session expired'))) {
        if (logout) logout();
      }
      // If token is missing, do not throw, just return null
      if (err.message && err.message.includes('No token found')) {
        return null;
      }
      throw err;
    }
  }, [logout]);

  const getLastFetched = (key) => cacheRef.current[`${key}_lastFetched`] || null;

  // Fetch Samsung service analysis data
  const fetchServiceAnalysis = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, samsungServiceData: { ...prev.samsungServiceData, loading: true } }));
      const data = await fetchData('samsungServiceData', { token, username, force });
      setDashboardState(prev => ({ ...prev, samsungServiceData: { loading: false, data, error: null } }));
      return data;
    } catch (error) {
      // Only set error if not a network error
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, samsungServiceData: { ...prev.samsungServiceData, loading: false, error: null } }));
        return null;
      }
      setDashboardState(prev => ({ ...prev, samsungServiceData: { ...prev.samsungServiceData, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername]);

  // Fetch Daily Progress data
  const fetchDailyProgress = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, dailyProgress: { ...prev.dailyProgress, loading: true } }));
      const data = await fetchData('dailyProgress', { token, username, force });
      setDashboardState(prev => ({
        ...prev,
        dailyProgress: { loading: false, data, error: null },
        serviceQueue: data && typeof data.totalQueue === 'number'
          ? { loading: false, data: { totalQueue: data.totalQueue }, error: null }
          : prev.serviceQueue
      }));
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, dailyProgress: { ...prev.dailyProgress, loading: false, error: null } }));
        return null;
      }
      setDashboardState(prev => ({ ...prev, dailyProgress: { ...prev.dailyProgress, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername]);

  // Fetch Estimate Data
  const fetchEstimateData = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, estimateData: { ...prev.estimateData, loading: true } }));
      const data = await fetchData('estimateData', { token, username, force });
      setDashboardState(prev => ({ ...prev, estimateData: { loading: false, data, error: null } }));
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, estimateData: { ...prev.estimateData, loading: false, error: null } }));
        return null;
      }
      setDashboardState(prev => ({ ...prev, estimateData: { ...prev.estimateData, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername]);

  // Fetch Service Queue data
  const fetchServiceQueue = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, serviceQueue: { ...prev.serviceQueue, loading: true } }));
      const dailyProgressData = cacheRef.current['dailyProgress']?.data || dashboardState.dailyProgress?.data;
      if (!force && dailyProgressData && typeof dailyProgressData.totalQueue === 'number') {
        setDashboardState(prev => ({ ...prev, serviceQueue: { loading: false, data: { totalQueue: dailyProgressData.totalQueue }, error: null } }));
        return { totalQueue: dailyProgressData.totalQueue };
      }
      const data = await fetchData('serviceQueue', { token, username, force });
      let totalQueue = data?.totalQueue;
      if (typeof totalQueue !== 'number' && data && typeof data === 'object') {
        totalQueue = data.dailyProgress?.totalQueue;
      }
      setDashboardState(prev => ({ ...prev, serviceQueue: { loading: false, data: { totalQueue: totalQueue ?? 0 }, error: null } }));
      return { totalQueue: totalQueue ?? 0 };
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, serviceQueue: { ...prev.serviceQueue, loading: false, error: null } }));
        return { totalQueue: 0 };
      }
      setDashboardState(prev => ({ ...prev, serviceQueue: { ...prev.serviceQueue, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername, dashboardState.dailyProgress]);

  // Fetch Scheduler Config with rotation settings
  const fetchSchedulerConfig = useCallback(async (force = false) => {
    if (!force && Date.now() - lastSchedulerSaveRef.current < 3000) return null;
    const { token } = getTokenAndUsername();
    if (!token) {
      console.warn('[DashboardDataContext] No token found, skipping fetchSchedulerConfig');
      return null;
    }
    try {
      setDashboardState(prev => ({ ...prev, scheduler: { ...prev.scheduler, loading: true } }));
      const url = `${process.env.REACT_APP_API_URL}/dashboard/scheduler/s-check`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch scheduler config');
      const data = await res.json();
      console.log('[DashboardDataContext] Fetched scheduler config:', data);
      setDashboardState(prev => ({ 
        ...prev, 
        scheduler: { loading: false, data, error: null },
        ...(data.rotation ? {
          rotation: {
            enabled: Boolean(data.rotation.enabled),
            interval: Number(data.rotation.interval || 300),
            isSaving: false
          }
        } : {})
      }));
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        console.warn('[DashboardDataContext] Network error while fetching scheduler config:', error);
        setDashboardState(prev => ({ ...prev, scheduler: { loading: false, data: null, error: null } }));
        return null;
      }
      console.error('[DashboardDataContext] Error fetching scheduler config:', error);
      setDashboardState(prev => ({ ...prev, scheduler: { loading: false, data: null, error: error.message } }));
      return null;
    }
  }, [getTokenAndUsername]);

  // Fetch open cases for product categories
  const fetchOpenCases = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, productCategoryData: { ...prev.productCategoryData, loading: true } }));
      const data = await fetchData('productCategory', { token, username, force });
      // Debug log: print the first few items and total count
      console.log('[DashboardDataContext] fetchOpenCases received', Array.isArray(data) ? data.slice(0, 5) : data, 'total:', Array.isArray(data) ? data.length : 'not array');
      setDashboardState(prev => ({ ...prev, productCategoryData: { loading: false, data, error: null } }));
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, productCategoryData: { ...prev.productCategoryData, loading: false, error: null } }));
        return [];
      }
      setDashboardState(prev => ({ ...prev, productCategoryData: { ...prev.productCategoryData, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername]);

  // Fetch user data for DataSourceWidget and others
  const fetchUserData = useCallback(async (force = false) => {
    try {
      const { token, username } = getTokenAndUsername();
      setDashboardState(prev => ({ ...prev, userData: { ...prev.userData, loading: true } }));
      const data = await fetchData('userData', { token, username, force });
      setDashboardState(prev => ({ ...prev, userData: { loading: false, data, error: null } }));
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message && error.message.includes('Failed to fetch')) {
        setDashboardState(prev => ({ ...prev, userData: { ...prev.userData, loading: false, error: null } }));
        return null;
      }
      setDashboardState(prev => ({ ...prev, userData: { ...prev.userData, loading: false, error: error.message } }));
      throw error;
    }
  }, [fetchData, getTokenAndUsername]);

  // Add setSchedulerRotation to context for auto-rotate toggle in Settings and DashboardScheduler
  const setSchedulerRotation = useCallback((rotationUpdate) => {
    // Log rotation update to help with debugging
    console.log('[DashboardDataContext] Updating rotation:', 
      typeof rotationUpdate === 'function' 
        ? 'Using function updater'
        : rotationUpdate
    );
    
    setDashboardState(prev => {
      const newRotation = {
        ...prev.rotation,
        ...(
          typeof rotationUpdate === 'function'
            ? rotationUpdate(prev.rotation)
            : rotationUpdate
        )
      };
      
      // Log before and after state
      console.log('[DashboardDataContext] Rotation enabled before:', prev.rotation?.enabled, 
                 'after:', newRotation?.enabled);
      
      // Persist rotation state to localStorage
      if (newRotation.enabled !== undefined) {
        localStorage.setItem('dashboardRotationEnabled', String(newRotation.enabled));
        console.log('[DashboardDataContext] Saved rotation enabled to localStorage:', newRotation.enabled);
      }
      
      // Always persist interval value to localStorage when provided
      if (newRotation.interval !== undefined) {
        localStorage.setItem('dashboardRotationInterval', String(newRotation.interval));
        console.log('[DashboardDataContext] Saved rotation interval to localStorage:', newRotation.interval);
      }
      
      return {
        ...prev,
        rotation: newRotation
      };
    });
  }, []);

  // Add setSchedulerEnabled function to update scheduler enabled state
  const setSchedulerEnabled = useCallback((enabled) => {
    console.log('[DashboardDataContext] Setting scheduler enabled:', enabled);
    setDashboardState(prev => ({
      ...prev,
      scheduler: {
        ...prev.scheduler,
        enabled: Boolean(enabled)
      }
    }));
  }, []);

  // Add setSchedulerRules function to update scheduler rules
  const setSchedulerRules = useCallback((rules) => {
    console.log('[DashboardDataContext] Setting scheduler rules:', rules);
    setDashboardState(prev => ({
      ...prev,
      scheduler: {
        ...prev.scheduler,
        rules: Array.isArray(rules) ? rules : []
      }
    }));
  }, []);

  // --- Global dashboard refresh with rate limit ---
  const lastGlobalRefreshRef = useRef([]); // array of timestamps
  const forceGlobalRefresh = useCallback(async () => {
    const now = Date.now();
    lastGlobalRefreshRef.current = lastGlobalRefreshRef.current.filter(ts => now - ts < 60 * 1000);
    if (lastGlobalRefreshRef.current.length >= 3) {
      throw new Error('You can refresh a maximum of 3 times per minute. Please try again later.');
    }
    lastGlobalRefreshRef.current.push(now);
    // Kjør alle relevante fetch-funksjoner parallelt, med force=true
    await Promise.all([
      fetchDailyProgress(true),
      fetchEstimateData(true),
      fetchServiceQueue(true),
      fetchSchedulerConfig(true),
      fetchServiceAnalysis(true),
      fetchOpenCases && fetchOpenCases(true),
      fetchUserData && fetchUserData(true)
    ]);
  }, [fetchDailyProgress, fetchEstimateData, fetchServiceQueue, fetchSchedulerConfig, fetchServiceAnalysis, fetchOpenCases, fetchUserData]);

  // Auto-refresh all dashboard data every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDailyProgress();
      fetchEstimateData();
      fetchServiceQueue();
      fetchSchedulerConfig();
      fetchServiceAnalysis();
      fetchOpenCases && fetchOpenCases();
      fetchUserData && fetchUserData();
    }, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, []); // <-- Only run once on mount, prevents runaway intervals

  // On mount: ensure all relevant data is fetched and cached at least once
  useEffect(() => {
    const ensureCached = async () => {
      const { token, username } = (() => {
        try { return getTokenAndUsername(); } catch { return { token: null, username: null }; }
      })();
      // Only fetch if not already cached
      const keys = [
        'productCategory',
        'estimateData',
        'dailyProgress',
        'serviceQueue',
        'samsungServiceData',
        'userData'
      ];
      for (const key of keys) {
        if (!cacheRef.current[key] || !cacheRef.current[key].data) {
          try {
            await fetchData(key, { token, username });
          } catch (e) {
            // Silent fail, will be retried by widgets if needed
          }
        }
      }
    };
    ensureCached();
  }, []);

  // NEW: Always sync productCategoryData state from cache if cache has data but state is empty
  useEffect(() => {
    const cacheData = cacheRef.current['productCategory']?.data;
    if (
      Array.isArray(cacheData) && cacheData.length > 0 &&
      (!dashboardState.productCategoryData || !Array.isArray(dashboardState.productCategoryData.data) || dashboardState.productCategoryData.data.length === 0)
    ) {
      setDashboardState(prev => ({
        ...prev,
        productCategoryData: { loading: false, data: cacheData, error: null }
      }));
    }
  }, [dashboardState.productCategoryData]);

  // In contextValue, always provide productCategoryData, falling back to productCategory if needed
  const contextValue = {
    ...dashboardState,
    productCategoryData: dashboardState.productCategoryData || dashboardState.productCategory || { loading: false, data: [], error: null },
    fetchOpenCases,
    fetchUserData,
    dailyProgressData: dashboardState.dailyProgress,
    fetchServiceAnalysis,
    fetchDailyProgress,
    fetchEstimateData,
    fetchServiceQueue,
    fetchSchedulerConfig,
    getLastFetched,
    setDashboardActive: setIsDashboardActive,
    setActiveWidgets: widgets => setDashboardState(prev => ({ ...prev, activeWidgets: Array.isArray(widgets) ? widgets : [] })),
    setLastZone: zoneId => setDashboardState(prev => ({ ...prev, lastZone: zoneId })),
    getCachedData: type => cacheRef.current[type]?.data || null,
    setSchedulerRotation,
    setSchedulerEnabled,
    setSchedulerRules,
    forceGlobalRefresh // <-- eksporter denne
  };

  return (
    <DashboardDataContext.Provider value={contextValue}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    throw new Error('useDashboardData must be used within a DashboardDataProvider');
  }
  return context;
}
